import React from 'react';
import { Button, Container } from '@/components/ui';

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Top-level error boundary.
 *
 * Catches rendering errors anywhere below it in the tree and shows a
 * friendly fallback instead of an unmounted, blank page. React error
 * boundaries must be class components (there is no hook equivalent) — see
 * `docs/TEMPLATE_IMPROVEMENT_RECOMMENDATIONS.md` §6. Wrapped once around
 * `<App />` in `main.tsx`.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: unknown, errorInfo: React.ErrorInfo): void {
        if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.error('Unhandled UI error:', error, errorInfo);
        }
    }

    private handleReload = (): void => {
        window.location.assign('/');
    };

    render(): React.ReactNode {
        if (this.state.hasError) {
            return (
                <section className="error-boundary">
                    <Container className="error-boundary-content">
                        <h1 className="error-boundary-title">Something went wrong</h1>
                        <p className="error-boundary-description">
                            An unexpected error occurred. Please try reloading the page.
                        </p>
                        <Button variant="primary" onClick={this.handleReload}>
                            Reload page
                        </Button>
                    </Container>
                </section>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
